import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import CompletePregnancyView from './CompletePregnancyView';
import PostpartumView from './PostpartumView';
import DatePicker from './DatePicker';
import LoadingSpinner from './common/LoadingSpinner';
import ConfirmationModal from './ConfirmationModal';
import { useFeedbackModal } from './common/useFeedbackModal';
import './PregnancyTracker.css';
import './ModernSetup.css';

function PregnancyTracker({ userId }) {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [mode, setMode] = useState('pregnancy'); // 'pregnancy' or 'postpartum'
  const { modal, openModal, closeModal } = useFeedbackModal();
  const [formData, setFormData] = useState({
    lmpDate: '',
    culture: 'ETHIOPIAN',
    dietaryRestrictions: 'NONE',
    hasNausea: false,
    hasAppetiteIssues: false,
    notes: ''
  });

  const loadProfile = useCallback(async () => {
    try {
      const profileData = await api.getPregnancyProfile(userId);
      setProfile(profileData);
      setShowSetup(false);
      // DatePicker accepts ISO format directly
      if (profileData.lmpDate) {
        setFormData(prev => ({
          ...prev,
          lmpDate: profileData.lmpDate
        }));
      }
      
      // Check if delivery date is set - if so, switch to postpartum mode
      if (profileData.deliveryDate) {
        setMode('postpartum');
      } else {
        setMode('pregnancy');
      }
      
    } catch (error) {
      // Silently handle 404 - expected when no profile exists yet
      // apiClient already handles logging appropriately
      const status = error.response?.status || error.status;
      if (status !== 404) {
        // Only log if it's not an expected 404
        console.log('No profile found, showing setup');
      }
      setShowSetup(true);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // DatePicker already provides ISO format
    const isoDate = formData.lmpDate;
    if (!isoDate || !isoDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      openModal({
        title: 'Invalid date',
        message: 'Please select a valid date.',
        type: 'error'
      });
      return;
    }
    
    try {
      const profileData = {
        userId,
        lmpDate: isoDate,
        eddDate: null, // Will be calculated automatically from LMP
        culture: formData.culture,
        dietaryRestrictions: formData.dietaryRestrictions,
        hasNausea: formData.hasNausea,
        hasAppetiteIssues: formData.hasAppetiteIssues,
        notes: formData.notes
      };
      
      console.log('💾 Saving pregnancy profile:', profileData);
      const saved = await api.savePregnancyProfile(profileData);
      console.log('✅ Profile saved:', saved);
      
      // Set profile and hide setup form - CompletePregnancyView will handle all data loading
      setProfile(saved);
      setShowSetup(false);
      
      // Don't call loadStatus here - CompletePregnancyView will load everything
      // This prevents race conditions where the profile isn't fully persisted yet
      console.log('🔄 Profile saved, CompletePregnancyView will load all data');
    } catch (error) {
      console.error('❌ Error saving profile:', error);
      openModal({
        title: 'Save failed',
        message: error.response?.data?.message || error.message || 'Failed to save pregnancy profile.',
        type: 'error'
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (loading) {
    return (
      <div className="pregnancy-tracker">
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }

  if (showSetup || !profile) {
    return (
      <div className="pregnancy-tracker">
        <div className="modern-setup-card">
          <div className="setup-header">
            <h2>🤱 {t('pregnancy.setup.title', 'Set Up Your Pregnancy Tracking')}</h2>
            <p className="setup-subtitle">
              {t('pregnancy.setup.description', 'Enter your LMP date to get started')}
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="modern-setup-form">
            <div className="modern-form-group">
              <label htmlFor="lmpDate">
                📅 {t('pregnancy.lmp', 'First Day of Last Menstrual Period')}
                <span className="required-asterisk"> *</span>
              </label>
              <DatePicker
                id="lmpDate"
                name="lmpDate"
                value={formData.lmpDate}
                onChange={handleChange}
                max={new Date().toISOString().split('T')[0]}
                placeholder="MM/DD/YYYY"
                required
              />
            </div>
            
            <button type="submit" className="btn-modern-primary">
              💾 {t('pregnancy.save', 'Start Tracking')}
            </button>
          </form>
        </div>

        <ConfirmationModal
          isOpen={modal.isOpen}
          onClose={closeModal}
          onConfirm={() => {}}
          title={modal.title}
          message={modal.message}
          type={modal.type}
          confirmText="OK"
          cancelText=""
        />
      </div>
    );
  }

  return (
    <div className="pregnancy-tracker">
      {/* Mode Switcher - Always visible */}
      <div className="mode-switcher" style={{ display: 'flex', visibility: 'visible' }}>
        <button
          className={`mode-button ${mode === 'pregnancy' ? 'active' : ''}`}
          onClick={() => {
            console.log('Switching to pregnancy mode');
            setMode('pregnancy');
          }}
        >
          🤱 {t('mode.pregnancy', 'Pregnancy')}
        </button>
        <button
          className={`mode-button ${mode === 'postpartum' ? 'active' : ''}`}
          onClick={() => {
            console.log('Switching to postpartum mode');
            setMode('postpartum');
          }}
          title={profile && !profile.deliveryDate ? t('postpartum.need.delivery.date', 'Click to enter delivery date and access postpartum mode') : ''}
        >
          👶 {t('mode.postpartum', 'Postpartum')}
        </button>
      </div>

      {/* Show appropriate view based on mode */}
        {mode === 'pregnancy' ? (
          <CompletePregnancyView userId={userId} initialProfile={profile} />
        ) : (
          <PostpartumView userId={userId} profile={profile} onProfileUpdate={loadProfile} />
        )}

      <ConfirmationModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        onConfirm={() => {}}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText="OK"
        cancelText=""
      />
    </div>
  );
}

export default PregnancyTracker;

